'use client';

import * as React from 'react';
import { cn } from '@/lib/core';
import { AppSettingsPropertiesModel } from '@/services/platform/api/model';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

export interface PhoneOption {
  label: string;
  value: string;
}

const FALLBACK_COUNTRY_CODES = [
  'Portugal_+351',
  'United Kingdom_+44',
  'United States_+1',
  'France_+33',
  'Spain_+34',
  'Germany_+49',
];

function buildOptions(appSettings?: AppSettingsPropertiesModel): PhoneOption[] {
  const prefixes = appSettings?.phonePrefixes as unknown as string[] | undefined;
  return (prefixes ?? FALLBACK_COUNTRY_CODES).map(p => {
    const sep = p.indexOf('_');
    const name = sep > 0 ? p.slice(0, sep) : '';
    const code = sep >= 0 ? p.slice(sep + 1) : p;
    return { label: name ? `${name} (${code})` : code, value: code };
  });
}

interface PhoneProps {
  id: string;
  appSettings?: AppSettingsPropertiesModel;
  countryCode: string;
  value: string;
  placeholder?: string;
  countryCodeError?: boolean;
  error?: boolean;
  disabled?: boolean;
  withoutPortal?: boolean;
  onCountryCodeChange: (value: string) => void;
  onChange: (value: string) => void;
  className?: string;
}

function Phone({
  id,
  appSettings,
  countryCode,
  value,
  placeholder,
  countryCodeError,
  error,
  disabled,
  withoutPortal = false,
  onCountryCodeChange,
  onChange,
  className,
}: PhoneProps) {
  return (
    <div className={cn('flex gap-4', className)}>
      <Select value={countryCode} onValueChange={onCountryCodeChange} disabled={disabled}>
        <SelectTrigger className="w-25.75 shrink-0" aria-invalid={countryCodeError || undefined}>
          <SelectValue>{countryCode}</SelectValue>
        </SelectTrigger>
        <SelectContent className="w-[calc(100vw-32px)]" withoutPortal={withoutPortal}>
          {buildOptions(appSettings).map(({ label, value: optValue }) => (
            <SelectItem key={optValue + label} value={optValue}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        id={id}
        type="tel"
        placeholder={placeholder ?? '000 000 000'}
        value={value}
        className="flex-1"
        error={error}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

export { Phone };
export type { PhoneProps };
